import {
  Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import type { PublicIntakeSample } from '../../types/intake';
import { testParamsSuffix } from './public/intakeFormState';

interface Props {
  samples: PublicIntakeSample[];
  /** testKey (upper) → display label ("Name (ASTM)"). */
  testLabels: Map<string, string>;
  /** sample-type code → description. */
  sampleTypeLabels: Map<string, string>;
}

/**
 * Read-only samples × tests view for the lab's intake review — one row per
 * sample, requested tests as chips (qty × name, DS stresses inline).
 */
export const IntakeSampleMatrix = ({ samples, testLabels, sampleTypeLabels }: Props) => {
  if (samples.length === 0) {
    return <Typography variant="body2" color="text.secondary">No samples on this request.</Typography>;
  }
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'grey.100' } }}>
            <TableCell>#</TableCell>
            <TableCell>Location ID</TableCell>
            <TableCell align="right">Top (m)</TableCell>
            <TableCell align="right">Base (m)</TableCell>
            <TableCell>Sample Ref</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Requested tests</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {samples.map((s, i) => (
            <TableRow key={i} hover>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{s.locationId ?? '—'}</TableCell>
              <TableCell align="right">{s.topDepth != null ? s.topDepth.toFixed(2) : '—'}</TableCell>
              <TableCell align="right">{s.baseDepth != null ? s.baseDepth.toFixed(2) : '—'}</TableCell>
              <TableCell>{s.sampleRef ?? '—'}</TableCell>
              <TableCell>{(s.sampleType && sampleTypeLabels.get(s.sampleType)) ?? s.sampleType ?? '—'}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {s.tests.length === 0 && (
                    <Typography variant="caption" color="text.secondary">none</Typography>
                  )}
                  {s.tests.map((t) => {
                    const label = testLabels.get(t.testKey.toUpperCase()) ?? 'Unknown test';
                    return (
                      <Chip
                        key={t.testKey}
                        size="small"
                        label={`${t.quantity} × ${label}${testParamsSuffix(t.params)}`}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    );
                  })}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
